import React from "react";
import { formatISO, isValid, parse, parseISO } from "date-fns";
import MaskedInput from "react-text-mask";
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe'

export interface MaskedDateInputProps {
    currentValue?: string;
    placeholder?: string;
    onBlur: (answer: string) => void;
    onResultChange: (answer: string) => void;
    locale?: string;
}

export default function (props: MaskedDateInputProps) {
    let locale = props.locale || navigator.language || "en-US";
    let formatPattern = getDateFormatPattern();

    //this needs to be lowercase. Uppercase will throw off the zero pad
    const autoCorrectedDatePipe = createAutoCorrectedDatePipe(formatPattern.toLowerCase());

    function getFormattedValue(dateToBeFormatted: string | undefined) {
        let currentAnswer = dateToBeFormatted; //value as it is keyed in
        if (currentAnswer) {
            let date = parseISO(currentAnswer);
            if (isValid(date)) {
                currentAnswer = formatDateToShortLocale(date);
            }
        }

        return currentAnswer;
    }

    function formatDateToShortLocale(date: Date) {
        //the format accomodates double digit month and days and 4 digit year
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(date);

        let newFormattedDate = formatPattern.replace('dd', day).replace('MM', month).replace('yyyy', year);
        return newFormattedDate;
    }

    function getDateFormatRegex() {
        const getPatternForPart = (part: Intl.DateTimeFormatPart) => {
            switch (part.type) {
                case 'day':
                case 'month':
                    return Array(2).fill(/\d/);
                case 'year':
                    return Array(4).fill(/\d/);
                case 'literal':
                    return part.value.split('');
                default:
                    return '';
            }
        };

        //Format based on locale date. Force double digit month and day and 4 digit year.
        const mask = new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).formatToParts(new Date())
            .flatMap(getPatternForPart);

        return mask;
    }

    function getDateFormatPattern() {
        const getPatternForPart = (part: Intl.DateTimeFormatPart) => {
            switch (part.type) {
                case 'day':
                    return `${'d'.repeat(2)}`;
                case 'month':
                    return `${'M'.repeat(2)}`;
                case 'year':
                    return `${'y'.repeat(4)}`;
                case 'literal':
                    return part.value;
                default:
                    return '';
            }
        };

        const formatPattern = new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).formatToParts(new Date())
            .map(getPatternForPart)
            .join('');

        return formatPattern;
    }

    let placeholder = props.placeholder ? props.placeholder : formatPattern.toUpperCase();

    return <MaskedInput className="date-picker survey-input survey-input-v2"
        pipe={autoCorrectedDatePipe}
        mask={getDateFormatRegex()}
        keepCharPositions={true}
        guide={true}
        placeholder={placeholder}
        value={getFormattedValue(props.currentValue)}
        onChange={(v: any) => {
            const date = parse(v.target.value, getDateFormatPattern(), new Date());
            if (isValid(date)) {
                props.onResultChange(formatISO(date).substring(0, 10));
            } else {
                props.onResultChange(v.target.value);
            }
        }}
        onBlur={() => props.onBlur(props.currentValue!)}
        inputMode="tel"
    />
}